import { currentUrl, URLX } from '../url';
import { findPageAndCacheIfAny } from '../current-page/state';
import { createSyncObservable, createEvent } from '../events';
import * as constants from '../constants';
import { getLocalStorageItem, setLocalStorageItem } from '../local-storage';

export type LoadFailure = {
  status: number;
  response: Response;
};

export const requestsInFlight = createSyncObservable<URLX[]>([])[0];
export const authRequired = createEvent<Response | undefined, boolean>()[0];

export function httpStatusOk(status: number) {
  return status >= 200 && status < 400;
}

export function get(url: URLX, headers?: { [name: string]: string }) {
  return load(url, 'get', undefined, headers);
}

export async function getJson<TData = any>(
  url: URLX,
  headers?: { [name: string]: string },
  includeDefaultHeaders = true,
): Promise<TData> {
  const result = await load(url, 'get', undefined, headers, includeDefaultHeaders).then(r => r.json());
  findPageAndCacheIfAny(result);
  return result;
}

export function post(url: URLX, body?: any, headers?: { [name: string]: string }, includeDefaultHeaders = true) {
  return load(url, 'post', body, headers, includeDefaultHeaders);
}

export async function postJson<TData = any>(
  url: URLX,
  body?: any,
  headers?: { [name: string]: string },
  includeDefaultHeaders = true,
): Promise<TData> {
  const result = await load(url, 'post', body, headers, includeDefaultHeaders).then(r => r.json());
  findPageAndCacheIfAny(result);
  return result;
}

type HttpMethod = 'get' | 'post';

function load(
  url: URLX,
  method: HttpMethod,
  body?: any,
  headers: { [name: string]: string } = {},
  includeDefaultHeaders = true,
  retryOnAuthRequired = true,
): Promise<Response> {
  requestsInFlight.update(current => {
    if (current.indexOf(url) === -1) {
      return [...current, url];
    }
    return current;
  });
  const removeFromRequestsInFlight = () => {
    requestsInFlight.update(current => {
      const index = current.indexOf(url);
      if (index !== -1) {
        const copy = current.slice();
        copy.slice(index, 1);
        return copy;
      }
      return current;
    });
  };

  headers = Object.assign(
    {
      [constants.CLIENT_VERSION_HEADER_NAME]: window.CURRENT_VERSION,
      // This is used to let the server and frontdoor detect which API version the client got when
      // the application loaded which is used to clear caches.
      [constants.API_VERSION_HEADER_NAME]: window.CURRENT_API_VERSION,
    },
    headers,
  );

  if (includeDefaultHeaders) {
    // Oddly enough these extra headers causes issues when trying to preload json responses.
    // Any extra headers will invalidate
    headers = Object.assign(
      {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      },
      headers,
    );
  }

  let promise = fetch(url.hiddenHref, {
    headers,
    credentials: 'same-origin',
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
    .then<any>(r => {
      if (serverHasDifferentMajorVersion(r)) {
        // Might seem drastic to do a full page reload when we get an Ajax response since it
        // will probably interrupt the user. But a major version change means that data structures
        // differ in a way that the client has to update. Major version changes should be few and far
        // between enough to not make this a usability issue.
        if (getLocalStorageItem('has-refresh-version', window.CURRENT_API_VERSION)) {
          const url = currentUrl();
          url.searchParams.set(
            constants.API_VERSION_QUERY_PARAM_NAME,
            r.headers.get(constants.API_VERSION_HEADER_NAME) ?? '0.0.0.0',
          );

          setLocalStorageItem('has-refresh-version', window.CURRENT_API_VERSION);
          window.location.href = url.toString();
        }

        // Return never resolving promise to pause the chain so the page can reload
        return new Promise(() => undefined);
      } else {
        return r;
      }
    })
    .then(async r => {
      if (httpStatusOk(r.status)) {
        return r;
      } else {
        if (r.status === 401 && authRequired.listeners() && retryOnAuthRequired) {
          try {
            const result = await authRequired.emit(r);
            if (result.some(r => r === true)) {
              return load(url, method, body, headers, includeDefaultHeaders, false);
            }
          } catch (e) {
            console.error(e);
          }
        }

        return Promise.reject({
          status: r.status,
          response: r,
        } as LoadFailure);
      }
    });

  promise.then(removeFromRequestsInFlight, removeFromRequestsInFlight);

  return promise;
}

function serverHasDifferentMajorVersion(response: Response) {
  // If the response is an error we might have failed to add the server version
  if (
    !response.headers.has(constants.VERSION_HEADER_NAME) &&
    !response.headers.has(constants.API_VERSION_HEADER_NAME)
  ) {
    if (response.ok || response.status === 503) {
      // If the response is missing the version header but not an error
      // the response has been intercepted by a maintenance page or something
      // like it which indicates that we should clear everything and reload.
      return true;
    }
    console.warn("Response didn't contain a server version in header");
    return false;
  }

  if (response.headers.has(constants.API_VERSION_HEADER_NAME)) {
    if (
      getVersionMismatch(response.headers.get(constants.API_VERSION_HEADER_NAME), window.CURRENT_API_VERSION) ===
      VersionMismatch.Major
    ) {
      return true;
    }
  }

  if (response.headers.has(constants.VERSION_HEADER_NAME)) {
    if (
      getVersionMismatch(response.headers.get(constants.VERSION_HEADER_NAME), window.CURRENT_VERSION) ===
      VersionMismatch.Major
    ) {
      return true;
    }
  }
  return false;
}

export const enum VersionMismatch {
  Major = 'major',
  Minor = 'minor',
  Patch = 'patch',
}

export function getVersionMismatch(serverVersion: string | null | undefined, clientVersion: string) {
  if (!serverVersion) {
    return VersionMismatch.Major;
  }
  const serverParts = serverVersion.split('.').map(Number);
  const clientParts = clientVersion.split('.').map(Number);

  if (serverParts[0] !== clientParts[0]) {
    return VersionMismatch.Major;
  }
  if (serverParts[1] !== clientParts[1]) {
    return VersionMismatch.Minor;
  }
  if (serverParts[2] !== clientParts[2]) {
    return VersionMismatch.Patch;
  }
}

export function getClientApiUrl(action: string): URLX {
  const url = new URLX(`/api/client/${action}`);
  url.searchParams.append('language', window.INITIAL_DATA.currentLanguage);
  return url;
}
